import { isNumber } from 'lodash';
import queryString from 'query-string';
import { isSafari } from '@/utils/browser-kit';
import { FOP } from '@/utils/fop-kit';

export const isMd5Uri = (uri: string = '') => {
    return uri.length === 32 && !isHttpUri(uri);
};

/**
 * 加载可供绘制的图片，Safari 等待解码结束后才交付资源
 *
 * 轮询补偿缺失的 load 事件；仅在 decode 不可用或失败时按完成状态与尺寸兜底。
 *
 * @param url - 原始图片地址
 * @param isImg - 是否启用 Safari 图片解码与轮询兼容处理
 * @returns 可供后续绘制使用的图片元素
 */
export function loadRawImageAsync(url: string, isImg = true): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const useSafariFallback = isSafari && isImg;
        let settled = false;
        let decoding = false;
        let decodeSettled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const cleanup = () => {
            if (timer !== undefined) clearTimeout(timer);
            img.onload = null;
            img.onerror = null;
        };
        const resolveImage = () => {
            if (settled) return;
            settled = true;
            cleanup();
            resolve(img);
        };
        const isReady = () => img.complete && Boolean(img.width || img.height);
        const decodeImage = () => {
            if (settled || decoding || typeof img.decode !== 'function') return;
            decoding = true;
            // onload 后仍可能有解码工作；解码成功才提前返回，失败则继续原有轮询。
            void Promise.resolve()
                .then(() => img.decode())
                .then(() => {
                    decodeSettled = true;
                    if (isReady()) resolveImage();
                })
                .catch(() => {
                    // 部分 Safari / SVG 无法通过 decode，仍按原兼容条件判断加载完成。
                    decodeSettled = true;
                });
        };
        const checkLoad = () => {
            if (settled) return;
            if (isReady()) {
                // 大图可能已拿到尺寸但仍在解码，不能让轮询抢先交付空白像素。
                if (typeof img.decode === 'function' && !decodeSettled) {
                    decodeImage();
                } else {
                    resolveImage();
                    return;
                }
            }
            timer = setTimeout(checkLoad, 200);
        };

        img.setAttribute('crossOrigin', 'anonymous');
        img.onload = useSafariFallback ? decodeImage : resolveImage;
        img.onerror = () => {
            if (settled) return;
            settled = true;
            cleanup();
            reject();
        };
        img.src = url;
        if (useSafariFallback && !settled) {
            timer = setTimeout(checkLoad, 200);
            if (isReady()) decodeImage();
        }
    });
}

/**
 * 判断是否为图片编辑器代理图片
 *
 * @param url 图片地址
 * @returns 若是代理图片则返回true，否则返回false
 */
export const isProxyImage = (url: string) => {
    return url.indexOf('/editsdk/fetch_image?url=') > 0;
};

/**
 * 判断是否为自有云服务图片，包含国内和海外站
 *
 * @param url 图片地址
 * @returns 若是自有云图片则返回true，否则返回false
 */
export const ownCloudImage = (url: string) => {
    const params = queryString.parseUrl(url);
    return (
        params.url.indexOf('.meitudata.com/') > 0 ||
        params.url.indexOf('.stariidata.com/') > 0 ||
        params.url.indexOf('.stariicloud.com/') > 0
    );
};

export const isWebpImage = (url: string) => {
    // 提取基础URL，去除查询参数和hash
    const baseUrl = url.split('?')[0].split('#')[0];
    if (/\.webp$/i.test(baseUrl)) {
        return true;
    }
    return false;
};

/**
 * 是否支持 WebP
 * 创建canvas需要进行缓存，避免创建不必要的canvas（不能放到isSupportWebp里）
 */
const canvasJudge = document.createElement('canvas').toDataURL('image/webp');
const isWebpSupport = canvasJudge.indexOf('data:image/webp;') === 0;

export const isSupportWebp = () => {
    return isWebpSupport;
};

/**
 * 判断是否 HTTP 前缀
 *
 * @param url - 链接地址
 */
export const isHttpUri = (url: string): boolean => {
    return /^https{0,1}:/i.test(url);
};

export const getPathname = (url: string) => {
    try {
        return new URL(url).pathname;
    } catch (_e) {
        return null;
    }
};

/**
 * 推荐使用 /lossless/80 解决 WebP 图片模糊问题；当前保持关闭以允许云处理异常时回退原图。
 */
const LOSSLESS_QUALITY = 0;

/**
 * 图片转成 WebP
 *
 * @param url - 图片链接地址
 * @param quality - 质量，默认90，权衡质量与体积
 */
export const convertImageToWebp = (url: string, quality = 90) => {
    if (
        !isSupportWebp() ||
        !isHttpUri(url) ||
        isProxyImage(url) ||
        !ownCloudImage(url) ||
        /\.svg$/.test(getPathname(url) ?? '') ||
        /token=/gi.test(url) ||
        isWebpImage(url)
    ) {
        return url;
    }

    const { fop, query, baseURL } = FOP.split(url);
    const fopParams = FOP.parse(fop);
    fopParams.imageView2 = fopParams.imageView2 || {};

    if (!isNumber(fopParams.imageView2.mode)) {
        fopParams.imageView2.mode = 2;
    }
    fopParams.imageView2.format = 'webp';
    fopParams.imageView2.q = quality;

    return FOP.stringify(fopParams, { baseURL, query });
};

/**
 * 为自有云图片生成指定宽度的缩略图 URL。
 */
export const imageConverter = (url: string, width: number, quality = 80) => {
    if (!isHttpUri(url) || isProxyImage(url) || !ownCloudImage(url) || /\.svg$/.test(url)) return url;

    const { fop, query, baseURL } = FOP.split(url);
    const fopParams = FOP.parse(fop);
    const isWebp = isWebpImage(url);
    fopParams.imageView2 = fopParams.imageView2 || {};
    fopParams.imageView2.mode = 2;
    fopParams.imageView2.w = width;
    fopParams.imageView2.ignoreError = 1;

    if (!isSupportWebp()) {
        fopParams.imageView2.q = quality;
        fopParams.imageView2.format = isWebp ? 'png' : undefined;
    } else if (!isWebp) {
        fopParams.imageView2.format = 'webp';
        fopParams.imageView2.q = quality;
    }

    if (LOSSLESS_QUALITY) {
        fopParams.imageView2.lossless = LOSSLESS_QUALITY;
    }

    return FOP.stringify(fopParams, { baseURL, query });
};

/**
 * 保留缩略尺寸等 FOP，仅移除 WebP 格式参数；不满足历史条件时移除全部 FOP。
 */
export const convertWebpToImage = (url: string) => {
    if (!isHttpUri(url) || isProxyImage(url) || !ownCloudImage(url) || /\.svg$/.test(url) || /token=/gi.test(url)) {
        return url;
    }

    const { fop, query, baseURL } = FOP.split(url);
    let fopParams = FOP.parse(fop);
    fopParams.imageView2 = fopParams.imageView2 || {};

    if (fopParams.imageView2.w && fopParams.imageView2.format === 'webp') {
        if (LOSSLESS_QUALITY) {
            fopParams.imageView2.lossless = undefined;
        }
        fopParams.imageView2.format = undefined;
    } else {
        fopParams = {};
    }

    return FOP.stringify(fopParams, { baseURL, query });
};

/**
 * 移除自有云图片的全部 FOP，保留普通 query 参数。
 */
export const convertImageToOrigin = (url: string) => {
    if (!isHttpUri(url) || isProxyImage(url) || !ownCloudImage(url) || /\.svg$/.test(url)) return url;

    const { query, baseURL } = FOP.split(url);
    return FOP.stringify({}, { baseURL, query });
};
